import React from 'react'
import { useIntl } from 'react-intl'

export interface CodeFieldProps {
  value: string
  onChange: (value: string) => void
  error?: string
  disabled?: boolean
  autoFocus?: boolean
  /** Label; defaults to the sign-in wording. */
  label?: string
  /** Helper line; pass `null` for none. */
  description?: string | null
  id?: string
  /** Whether a recovery code is acceptable here (every screen but enrolment's first code). */
  acceptsRecoveryCode?: boolean
}

/**
 * The one input every second-factor prompt uses. A 6-digit authenticator code
 * and a recovery code (`xxxxx-xxxxx`, any case, dash optional) go in the SAME
 * field — there is no "use a recovery code instead" mode picker, because the
 * backend tells them apart.
 */
export const CodeField: React.FC<CodeFieldProps> = ({
  value,
  onChange,
  error,
  disabled,
  autoFocus,
  label,
  description,
  id = 'two-factor-code',
  acceptsRecoveryCode = true,
}) => {
  const intl = useIntl()
  const helper =
    description === null
      ? undefined
      : description ??
        intl.formatMessage({ id: acceptsRecoveryCode ? 'TWO_FACTOR.code.helper' : 'TWO_FACTOR.code.helperCodeOnly' })
  return (
    <div className='mb-4 text-start'>
      <label className='form-label fw-semibold' htmlFor={id}>
        {label ?? intl.formatMessage({ id: 'TWO_FACTOR.code.label' })}
      </label>
      <input
        id={id}
        name='code'
        type='text'
        dir='ltr'
        className={`form-control form-control-lg ${error ? 'is-invalid' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete='one-time-code'
        autoCapitalize='none'
        autoCorrect='off'
        spellCheck={false}
        inputMode={acceptsRecoveryCode ? 'text' : 'numeric'}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : helper ? `${id}-help` : undefined}
      />
      {error ? (
        <div id={`${id}-error`} className='invalid-feedback d-block' role='alert'>
          {error}
        </div>
      ) : helper ? (
        <div id={`${id}-help`} className='form-text'>
          {helper}
        </div>
      ) : null}
    </div>
  )
}
